// src/components/JiraConfigurationModal.js
import React, { useState } from "react";
import { XCircle, ExternalLink, AlertCircle } from "react-feather";
import { useApi } from "../hooks/useApi";
import { useModuleAvailability } from "../hooks/useModuleAvailability";
import UnavailableNote from "./common/UnavailableNote";

const JiraConfigurationModal = ({ onClose, onSuccess }) => {
  const [instanceUrl, setInstanceUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const apiCall = useApi();
  // Ticketing needs internet access; on Restricted / AirGapped installations connecting is disabled.
  const { isModuleAvailable } = useModuleAvailability();
  const unavailable = !isModuleAvailable("ticketing");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (unavailable) return;

    if (!instanceUrl.trim()) {
      setError("Please enter your Jira instance URL");
      return;
    }

    // Normalize the URL
    let normalizedUrl = instanceUrl.trim()
      .replace(/^https?:\/\//, "")
      .replace(/\/$/, "");

    // Validate it looks like an Atlassian URL
    if (!normalizedUrl.endsWith(".atlassian.net")) {
      setError("Please enter a valid Jira Cloud URL (e.g., your-company.atlassian.net)");
      return;
    }

    setLoading(true);

    try {
      // Start the OAuth flow
      const response = await apiCall("POST", "/integrations/jira/authorize", {
        instanceUrl: normalizedUrl
      });

      // Store the state and instance URL for after OAuth callback
      sessionStorage.setItem("jira_oauth_state", response.state);
      sessionStorage.setItem("jira_instance_url", normalizedUrl);

      // Redirect to Atlassian for authorization
      window.location.href = response.authorizationUrl;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to start Jira authorization");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-content-primary">Configure Jira Integration</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={loading}
          >
            <XCircle size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Info Box */}
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              Connect your Jira Cloud instance to create issues directly from Axaris remediations.
            </p>
          </div>
          {unavailable && <UnavailableNote className="mb-4" />}

          {/* Error */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Instance URL Input */}
          <div className="mb-4">
            <label
              htmlFor="instanceUrl"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Jira Cloud Instance URL
            </label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                https://
              </span>
              <input
                type="text"
                id="instanceUrl"
                value={instanceUrl}
                onChange={(e) => setInstanceUrl(e.target.value)}
                placeholder="your-company.atlassian.net"
                className="flex-1 block w-full rounded-none rounded-r-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={loading}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Enter your Jira Cloud URL (e.g., your-company.atlassian.net)
            </p>
          </div>

          {/* OAuth Info */}
          <div className="mb-6 text-sm text-gray-600">
            <p>
              Clicking "Connect" will redirect you to Atlassian to authorize Axaris.
              You'll be asked to grant permission to:
            </p>
            <ul className="mt-2 ml-4 list-disc text-gray-500">
              <li>Read your Jira projects and issues</li>
              <li>Create and update issues</li>
            </ul>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              disabled={loading || unavailable}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Connecting...
                </>
              ) : (
                <>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Connect to Jira
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JiraConfigurationModal;
